// useGitConfigs - preloads the parameter values the package has maintained in Git.
//   GET /api/cicd/package-configs?spaceId&packageId&packageName&kennungen&artifactIds
//
// The wizard used to read its values from the tenants only, and the snapshot then rewrote the Git
// file with whatever was on screen - a QAS transport thus deleted the maintained PROD values. Git
// is the truth now, and it is read HERE, BEFORE the configuration view prefills its fields.
//
// The request names the source package and the kennungen, not finished target package ids: the
// naming rule belongs in the backend, where the snapshot applies it too.

import { useCallback, useState } from "react";
import { api } from "../../../utils/helpers";
import { useTransportV2, usesWerke, activeWerke, buckets } from "../context/TransportV2Context";
import { requestPlantNaming, plantTargetRevisions } from "../utils/plantNaming";

import { parameterRequirement } from "../utils/parameterRequirement";

export function useGitConfigs() {
  const { state, dispatch } = useTransportV2();
  const requirement = parameterRequirement(state, buckets(state));
  const [failure, setFailure] = useState(null);
  const kennungen = usesWerke(state) ? activeWerke(state).map(w => w.kennung).filter(Boolean) : [];
  const artefakte = Object.entries(state.sel || {}).filter(([, choices]) => Object.values(choices || {}).some(Boolean)).map(([id]) => id);
  const scopeKey = JSON.stringify([state.spaceId, state.sourceInstanceId, state.pkgId, state.pkgVersion, state.pkgMod,
    state.space?.qasInstance?.id, state.plantNaming, kennungen, artefakte,
    Object.entries(state.precheckByBucket || {}).map(([key, entry]) => [key, entry.plantTargetRevision]), requirement]);
  const errorScope = JSON.stringify([scopeKey, state.configReadGeneration]);

  const laden = useCallback(async ({ signal } = {}) => {
    if (signal?.aborted) return false;
    setFailure(null);
    if (requirement === "not-required") {
      dispatch({ type: "SET_GIT_CFG", payload: { werke: {}, unvollstaendig: false, scopeKey } });
      dispatch({ type: "SET_GIT_CFG_STATE", payload: "not-required" });
      return true;
    }
    // Nothing to load without a space or a package - but the state MUST still advance, or the
    // configuration view waits forever. An instance group without a space is selectable and yields
    // spaceId = null; the state then stayed "idle" and the footer reported everything as set while
    // the transport ran without any parameters.
    if (!state.spaceId || !state.pkgId) {
      dispatch({ type: "SET_GIT_CFG_STATE", payload: "leer" });
      return true;
    }
    dispatch({ type: "SET_GIT_CFG_STATE", payload: "laedt" });
    try {
      const q = new URLSearchParams({
        spaceId: state.spaceId,
        sourceInstanceId: state.sourceInstanceId || "",
        targetInstanceId: state.space?.qasInstance?.id || "",
        packageId: state.pkgId,
        packageName: state.pkgName || state.pkgId,
        kennungen: kennungen.join(","),
        artifactIds: artefakte.join(","),
        plantNaming: JSON.stringify(requestPlantNaming(state.plantNaming)),
      });
      const revisions = plantTargetRevisions(state);
      if (revisions !== undefined) q.set("plantTargetRevisions", JSON.stringify(revisions));
      const r = await api(`/api/cicd/package-configs?${q.toString()}`, { retry: false, signal });
      if (signal?.aborted) return false;
      if (!r?.werke || typeof r.werke !== "object" || Array.isArray(r.werke)) throw Object.assign(new Error("Invalid Git parameter response"), { code: "PACKAGE_CONFIG_READ_FAILED" });
      dispatch({ type: "SET_GIT_CFG", payload: { werke: r.werke, unvollstaendig: !!r.unvollstaendig, scopeKey } });
      return !r.unvollstaendig;
    } catch (error) {
      // Failed reads must not fall back to tenant values and overwrite maintained Git values.
      if (!signal?.aborted) {
        const code = error?.name === "AbortError" ? "TRANSPORT_READ_TIMEOUT"
          : /^[A-Z][A-Z0-9_]{1,79}$/.test(error?.code || "") ? error.code : "PACKAGE_CONFIG_READ_FAILED";
        setFailure({ scope: errorScope, code });
        dispatch({ type: "SET_GIT_CFG_STATE", payload: "fehler" });
      }
      return false;
    }
  }, [state, dispatch, scopeKey, requirement, errorScope]); // eslint-disable-line react-hooks/exhaustive-deps

  return { laden, scopeKey, requirement, errorCode: failure?.scope === errorScope ? failure.code : null };
}
